import { Button, DeliverablePillSummary, Input, Table } from "@shared/ui";
import classNames from "classnames";

import {
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  PenLine,
  X
} from "lucide-react";
import React, {
  JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./styles.module.scss";

import { useGetBriefDeliverableConfig } from "@entities/brief/lib/hooks/useGetBriefDeliverableConfig";
import { useGetMe } from "@entities/Onboarding/lib/hooks/useGetMe";

import { MoreOutlined } from "@ant-design/icons";
import { BriefDeliverableTaskTypeEnum } from "@entities/brief/lib/constants";
import { BriefCreatorColumn } from "@features/breifCreatorColumn/BriefCreatorColumn";
import { BriefOpsAssignedFilter } from "@features/briefOpsAssignedFilter/BriefOpsAssignedFilter";
import CampaignBriefInfluencerDrawer from "@features/CampaignBriefInfluencerDrawer";
import { CampaignExecutionGantt } from "@features/CampaignExecutionGantt";
import { VendorPayment } from "@features/vendorPayment/VendorPayment";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { useTableRowDragSelection } from "@shared/lib/hooks";
import {
  BriefCampaignInf,
  FinanceBasePayoutFlags,
  FinanceGstPayoutFlags,
  FinanceInvoiceFlags,
  IBriefCampaignInfluencerSearchRequestV2,
  ISearchFacets,
} from "@shared/lib/types/briefCampains";
import {
  CustomFieldType,
  IFieldConfig,
} from "@shared/lib/types/briefInfluencerV2";
import { formatData } from "@shared/lib/utils/formatData";
import { SearchMultiSelectFilter } from "@shared/ui/filters/SearchMultiSelectFilter";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import CampaignDraftDrawer from "@widgets/CampaignDraftDrawer";
import { ExecutionStatusFilter } from "@widgets/CampaignExecution/constants";
import CampaignScriptingDrawer from "@widgets/CampaignScriptingDrawer";
import { Drawer, Dropdown, Flex, InputNumber, Space, Tooltip } from "antd";
import { TableProps } from "antd/es/table";
import {
  buildExecutionTreeData,
  CampaignExecutionTableRow,
  OpenTaskDrawerParams,
} from "../../lib";
import { CampaignExecutionDeliverableTabs } from "./CampaignExecutionDeliverableTabs";
import {
  DeliverableTitleCell,
  DeliverableViewCell,
} from "./DeliverableCells";
import { CampaignExecutionEmptyState } from "./EmptyState";
import { ExecutionInfluencerStatus } from "./ExecutionInfluencerStatus";
import { BillingDetailsCell, PaymentAmountCell } from "./PaymentCells";
import { buildStageColumns } from "./stageColumns";
import { useExecutionRowSelection } from "./useExecutionRowSelection";

import {
  SORT_ORDER
} from "@entities/brief/lib/constants";
import { useEditBriefCampaignDataFieldV2Data } from "@entities/brief/lib/hooks/campaigns/useEditBriefCampaignDataFieldV2Data";
import { NotesCell } from "@entities/brief/ui/NotesCell/NotesCell";
import { useGetCampaignDynamicFacets } from "@entities/campaigns/lib/hooks/useGetCampaignDynamicFacets";
import useGetDeliverableConfigurators from "@entities/deliverableConfigurator/lib/hooks/useGetDeliverableConfigurators";
import { trimText } from "@shared/lib/utils/trimtext";
import { ColumnSortUI } from "@shared/ui/filters/ColumnSortUI";
import NumberRangeFilter from "@shared/ui/filters/NumberRangeFilter";


interface CampaignExecutionTableProps {
  campaignInfluencers: BriefCampaignInf[];

  onLoadMore: () => void;

  title?: JSX.Element;
  activeStatusFilter: ExecutionStatusFilter;
  isLoadingFirstPage: boolean;
  isLoadingMore: boolean;
  facets: {
    _id: string;
    completedCount: number;
    count: number;
  }[];
  showPaymentColumns?: boolean;
  isVendorBillingAttached?: boolean;
  setIsVendorBillingAttached?: (attached: boolean | undefined) => void;
  financeFlags?: IBriefCampaignInfluencerSearchRequestV2["financeFlags"];
  setFinanceFlags?: (
    flags: IBriefCampaignInfluencerSearchRequestV2["financeFlags"]
  ) => void;
  selectAll: boolean;
  checkedList: string[];
  uncheckList: string[];
  setSelectAll: (value: boolean) => void;
  setCheckedList: (list: string[]) => void;
  setUncheckList: (list: string[]) => void;
  totalCount?: number;
  assignedOperationsPoc?: string[];
  setAssignedOperationsPoc?: (poc: string[] | undefined) => void;
  pocOptions?: ISearchFacets["assignedOperationsPoc"];
  filters?: IBriefCampaignInfluencerSearchRequestV2;
  setFilters?: React.Dispatch<
    React.SetStateAction<IBriefCampaignInfluencerSearchRequestV2>
  >;
  hasActiveSearch?: boolean;
  viewMode?: "table" | "gantt";
  onViewModeChange?: (mode: "table" | "gantt") => void;
  searchQuery?: string;
  activePendingReviewFlag?: string | null;
}

export function CampaignExecutionTable({
  campaignInfluencers,
  isLoadingFirstPage,
  isLoadingMore,
  onLoadMore,
  title,
  activeStatusFilter,
  facets,
  showPaymentColumns,
  isVendorBillingAttached,
  setIsVendorBillingAttached,
  financeFlags,
  setFinanceFlags,
  selectAll,
  checkedList,
  uncheckList,
  setSelectAll,
  setCheckedList,
  setUncheckList,
  totalCount,
  assignedOperationsPoc,
  setAssignedOperationsPoc,
  pocOptions,
  filters,
  setFilters,
  hasActiveSearch = false,
  viewMode: controlledViewMode,
  onViewModeChange,
  searchQuery,
  activePendingReviewFlag,
}: CampaignExecutionTableProps) {
  const { briefId, brief } = useBriefStore();
  const [internalViewMode, setInternalViewMode] = useState<"table" | "gantt">("table");
  const viewMode = controlledViewMode ?? internalViewMode;
  const setViewMode = onViewModeChange ?? setInternalViewMode;
  const { dynamicFacets } = useGetCampaignDynamicFacets(briefId);
  // const [openPackagesProps, setOpenPackagesProps] = useState<{
  //   open: boolean;
  //   selectedBriefInfluencer?: IBriefCampaignBriefInfluencer;
  // }>({
  //   open: false,
  //   selectedBriefInfluencer: undefined,
  // });

  const isLoadingRef = useRef(false);

  const { config, isFetching: isFetchingConfig } =
    useGetBriefDeliverableConfig(briefId);

  const { editDataField, isEditing, isEdited } =
    useEditBriefCampaignDataFieldV2Data(briefId);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [highlightedRecordId, setHighlightedRecordId] = useState<string>("");
  const [
    openCampaignBriefInfluencerDrawerProps,
    setOpenCampaignBriefInfluencerDrawerProps,
  ] = useState<{
    open: boolean;
    selectedCampaignInfluencerId?: string;
    deliverableConfigRefId?: string;
    taskId?: string;
  }>({
    open: false,
    selectedCampaignInfluencerId: undefined,
    deliverableConfigRefId: undefined,
    taskId: undefined,
  });

  const [paymentModalProps, setPaymentModalProps] = useState<{
    visible: boolean;
    influencerId?: string;
  }>({
    visible: false,
    influencerId: undefined,
  });

  const [draftDrawerProps, setDraftDrawerProps] = useState<{
    open: boolean;
    draftId: string;
  }>({
    open: false,
    draftId: "",
  });

  const [scriptDrawerProps, setScriptDrawerProps] = useState<{
    open: boolean;
    scriptingId: string;
  }>({
    open: false,
    scriptingId: "",
  });

  const handleOpenTaskDrawer = useCallback(
    ({
      campaignInfluencerId,
      deliverableConfigRefId,
      taskId,
      taskType,
      taskForm,
    }: OpenTaskDrawerParams) => {
      const isDraft =
        taskType === BriefDeliverableTaskTypeEnum.Draft ||
        (typeof taskType === "string" && taskType.toLowerCase() === "draft");

      const isScript =
        taskType === BriefDeliverableTaskTypeEnum.Script ||
        (typeof taskType === "string" && taskType.toLowerCase() === "script");

      if (isDraft && taskForm?.draftingId) {
        setDraftDrawerProps({
          open: true,
          draftId: taskForm.draftingId,
        });
        return;
      }

      if (isScript && taskForm?.scriptingId) {
        setScriptDrawerProps({
          open: true,
          scriptingId: taskForm.scriptingId,
        });
        return;
      }

      setOpenCampaignBriefInfluencerDrawerProps({
        open: true,
        selectedCampaignInfluencerId: campaignInfluencerId,
        deliverableConfigRefId,
        taskId,
      });
    },
    []
  );

  const selectedDeliverableId = useMemo(() => {
    if (!filters?.deliverables) return null;
    const keys = Object.keys(filters.deliverables);
    return keys.length > 0 ? keys[0] : null;
  }, [filters?.deliverables]);

  const handleSelectDeliverable = useCallback(
    (deliverableId: string | null) => {
      setSelectAll(false);
      setCheckedList([]);
      setUncheckList([]);
      if (setFilters) {
        setFilters((prev) => ({
          ...prev,
          deliverables: deliverableId
            ? {
                [deliverableId]: prev.deliverables?.[deliverableId] || {},
              }
            : {},
        }));
      }
    },
    [setFilters, setSelectAll, setCheckedList, setUncheckList]
  );

  const displayedCampaignInfluencers = useMemo(() => {
    if (!selectedDeliverableId) return campaignInfluencers;
    return campaignInfluencers.filter((c) =>
      c.deliverables?.some(
        (d) =>
          d._id === selectedDeliverableId ||
          (d as any).deliverableConfigRefId === selectedDeliverableId
      )
    );
  }, [campaignInfluencers, selectedDeliverableId]);

  const executionTreeData: CampaignExecutionTableRow[] = useMemo(() => {
    return buildExecutionTreeData({
      creators: displayedCampaignInfluencers,
      config,
      selectedDeliverableId,
    });
  }, [displayedCampaignInfluencers, config, selectedDeliverableId]);

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const isFirstExpandedLoadRef = useRef(true);

  const allRowKeys = useMemo(
    () =>
      executionTreeData
        .filter((r) => r.children && r.children.length > 0)
        .map((r) => r._id)
        .filter(Boolean),
    [executionTreeData]
  );

  useEffect(() => {
    if (allRowKeys.length > 0) {
      if (isFirstExpandedLoadRef.current) {
        setExpandedRowKeys(allRowKeys);
        isFirstExpandedLoadRef.current = false;
      } else {
        setExpandedRowKeys((prev) => {
          if (prev.length === 0) return prev;
          const prevSet = new Set(prev);
          const newKeys = allRowKeys.filter((id) => !prevSet.has(id));
          return newKeys.length > 0 ? [...prev, ...newKeys] : prev;
        });
      }
    }
  }, [allRowKeys]);

  useEffect(() => {
    if (allRowKeys.length > 0) {
      setExpandedRowKeys(allRowKeys);
    }
  }, [selectedDeliverableId, allRowKeys]);

  const isAllExpanded = useMemo(() => {
    if (allRowKeys.length === 0) return false;
    return allRowKeys.every((key) => expandedRowKeys.includes(key));
  }, [allRowKeys, expandedRowKeys]);

  const handleToggleExpandAll = useCallback(() => {
    if (isAllExpanded) {
      setExpandedRowKeys([]);
    } else {
      setExpandedRowKeys(allRowKeys);
    }
  }, [isAllExpanded, allRowKeys]);

  const rowSelection = useExecutionRowSelection<CampaignExecutionTableRow>({
    rows: displayedCampaignInfluencers,
    totalCount: selectedDeliverableId
      ? displayedCampaignInfluencers.length
      : totalCount || 0,
    selectAll,
    checkedList,
    uncheckList,
    setSelectAll,
    setCheckedList,
    setUncheckList,
    hasActiveSearch,
    highlightedRecordId,
  });

  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStartIndex, setSelectionStartIndex] = useState<number | null>(
    null
  );

  const checkedListRef = useRef(checkedList);
  checkedListRef.current = checkedList;

  const uncheckListRef = useRef(uncheckList);
  uncheckListRef.current = uncheckList;

  const handleMouseDown = (index: number, event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    const isCheckbox =
      target.closest(".ant-checkbox") ||
      target.closest(".ant-checkbox-wrapper") ||
      target.closest(".ant-table-selection-column");

    if (!isCheckbox || event.button !== 0) {
      return;
    }

    event.stopPropagation();

    const row = displayedCampaignInfluencers[index];
    if (!row) return;

    setIsSelecting(true);
    setSelectionStartIndex(index);

    if (selectAll) {
      setUncheckList(uncheckList.filter((key) => key !== row._id));
    } else {
      setCheckedList([
        ...checkedList.filter((key) => key !== row._id),
        row._id,
      ]);
    }
  };

  const handleMouseMove = useCallback(
    (index: number) => {
      if (
        isSelecting &&
        selectionStartIndex !== null &&
        index >= 0 &&
        index < displayedCampaignInfluencers.length
      ) {
        const start = Math.min(selectionStartIndex, index);
        const end = Math.max(selectionStartIndex, index);

        const rowsToSelect = displayedCampaignInfluencers.slice(start, end + 1);
        const keysToSelect = rowsToSelect.map((row) => row._id);

        if (selectAll) {
          setUncheckList(
            uncheckListRef.current.filter((key) => !keysToSelect.includes(key))
          );
        } else {
          setCheckedList([
            ...checkedListRef.current.filter(
              (key) => !keysToSelect.includes(key)
            ),
            ...keysToSelect,
          ]);
        }
      }
    },
    [
      isSelecting,
      selectionStartIndex,
      displayedCampaignInfluencers,
      selectAll,
      setCheckedList,
      setUncheckList,
    ]
  );

  useTableRowDragSelection(displayedCampaignInfluencers, {
    scrollRootRef: scrollRef,
    isSelecting,
    onSelectRange: handleMouseMove,
  });

  const handleMouseEnterRow = useCallback(
    (index: number) => {
      handleMouseMove(index);
    },
    [handleMouseMove]
  );

  const handleMouseUp = useCallback((event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setIsSelecting(false);
    setSelectionStartIndex(null);
  }, []);

  useEffect(() => {
    if (!isSelecting) return;

    const onDocumentMouseUp = () => {
      setIsSelecting(false);
      setSelectionStartIndex(null);
    };

    document.addEventListener("mouseup", onDocumentMouseUp);
    return () => document.removeEventListener("mouseup", onDocumentMouseUp);
  }, [isSelecting]);

  const { userDetails } = useGetMe();

  const { deliverableConfiguratorsData } = useGetDeliverableConfigurators();
  const configurators = deliverableConfiguratorsData?.data?.data || [];

  const [editFieldProps, setEditFieldProps] = useState<{
    campaignInfluencerId: string;
    key: string;
    value?: string | number; //| string[] | { url: string; type: UploadType }[];
    fieldType?: CustomFieldType;
    originalValue?: string | number; //| string[] | { url: string; type: UploadType }[];
  }>({
    campaignInfluencerId: "",
    key: "",
    value: undefined,
    fieldType: undefined,
    originalValue: undefined,
  });

  useEffect(() => {
    if (!editFieldProps.campaignInfluencerId) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setEditFieldProps({
          campaignInfluencerId: "",
          key: "",
          value: undefined,
          fieldType: undefined,
          originalValue: undefined,
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [editFieldProps.campaignInfluencerId]);

  const visibleCustomFields = useMemo(() => {
    let customFields: IFieldConfig[] = [];
    customFields = [
      ...customFields,
      ...(brief?.dataFieldsV2?.filter(
        (field) => field.isSpecialField && field.isShared
      ) || []),
    ];
    return customFields;
  }, [brief?.dataFieldsV2, userDetails]);

  const columns: TableProps<CampaignExecutionTableRow>["columns"] = [
    Table.SELECTION_COLUMN,
    {
      title: "",
      key: "expand_action",
      fixed: "left" as const,
      width: 22,
      className: styles.expandActionCell,
      onHeaderCell: () => ({
        className: styles.expandActionHeaderCell,
      }),
      render: (_, record) => {
        if (record.isDeliverableRow || !record.children?.length) return null;
        const isExpanded = expandedRowKeys.includes(record._id);
        return (
          <Button
            type="text"
            size="small"
            className={styles.expandArrowBtn}
            onClick={(e) => {
              e.stopPropagation();
              setExpandedRowKeys((prev) =>
                isExpanded
                  ? prev.filter((k) => k !== record._id)
                  : [...prev, record._id]
              );
            }}
            icon={
              isExpanded ? (
                <ChevronDown size={14} className={styles.expandArrowIcon} />
              ) : (
                <ChevronRight size={14} className={styles.expandArrowIcon} />
              )
            }
          />
        );
      },
    },
    {
      title: "Creator Profile",
      dataIndex: "influencerName",
      key: "creator",
      fixed: "left" as const,
      width: 200,
      render: (_: unknown, record: CampaignExecutionTableRow) => {
        if (record.isDeliverableRow && record.deliverableItem) {
          return (
            <DeliverableTitleCell
              item={record.deliverableItem}
              onOpenDeliverable={(delId) => {
                setOpenCampaignBriefInfluencerDrawerProps({
                  open: true,
                  selectedCampaignInfluencerId:
                    record.deliverableItem?.parentRecord?._id || record._id,
                  deliverableConfigRefId: delId,
                });
              }}
              onOpenTaskDrawer={handleOpenTaskDrawer}
            />
          );
        }

        return (
          <BriefCreatorColumn
            influencerId={record.influencerId}
            influencerName={record.influencerName}
            username={record.socialMedia?.username}
            platform={record.socialMedia?.platform}
            profilePicUrl={record.socialMedia?.profilePicUrl}
            link={record.socialMedia?.link}
            isCreatorDeclined={record.isCreatorDeclined}
            isBrandDeclined={record.isBrandDeclined}
            creatorDeclinedReason={record.creatorDeclinedReason}
            brandDeclinedReason={record.brandDeclinedReason}
          />
        );
      },
    },
    ...(showPaymentColumns
      ? [
          {
            title: "Payments",
            key: "payments",
            onHeaderCell: () => ({
              style: {
                background: "var(--bg-primary)",
                textAlign: "center" as const,
                fontWeight: 500,
                // zIndex: 101,
                transition: "background-color 0.3s ease",
              },
            }),
            onCell: () => ({
              style: {
                background: "var(--bg-primary)",
                padding: "8px 12px",
                transition: "background-color 0.3s ease",
              },
            }),
            children: [
              {
                title: "Billing",
                dataIndex: "billingDetails",
                key: "billingDetails",
                width: 220,
                filterCount: isVendorBillingAttached === undefined ? 0 : 1,
                filterDropdownProps: {
                  destroyPopupOnHide: true,
                },
                filtered: isVendorBillingAttached !== undefined,
                filterDropdown: (props: {
                  confirm: () => void;
                  close: () => void;
                }) => (
                  <SearchMultiSelectFilter
                    confirm={props.confirm}
                    close={props.close}
                    searchPlaceholder="Search Billing Status"
                    options={[
                      { label: "Billing Attached", value: "Billing Attached" },
                      { label: "Billing Pending", value: "Billing Pending" },
                      { label: "All", value: "All" },
                    ]}
                    selectedValues={
                      isVendorBillingAttached === undefined
                        ? ["All"]
                        : isVendorBillingAttached === true
                          ? ["Billing Attached"]
                          : ["Billing Pending"]
                    }
                    onApply={(value) => {
                      if (
                        value.includes("All") ||
                        value.length === 0 ||
                        (value.includes("Billing Attached") &&
                          value.includes("Billing Pending"))
                      ) {
                        setIsVendorBillingAttached?.(undefined);
                      } else if (value.includes("Billing Attached")) {
                        setIsVendorBillingAttached?.(true);
                      } else if (value.includes("Billing Pending")) {
                        setIsVendorBillingAttached?.(false);
                      } else {
                        setIsVendorBillingAttached?.(undefined);
                      }
                    }}
                  />
                ),
                render: (_text: unknown, record: CampaignExecutionTableRow) => {
                  if (record.isDeliverableRow) return null;
                  return <BillingDetailsCell record={record} />;
                },
              },
              {
                title: "Invoice",
                dataIndex: "invoice",
                key: "invoice",
                width: 220,
                filterCount: financeFlags?.invoice?.statuses?.length || 0,
                filterDropdownProps: {
                  destroyPopupOnHide: true,
                },
                filtered: !!financeFlags?.invoice?.statuses?.length,
                filterDropdown: (props: {
                  confirm: () => void;
                  close: () => void;
                }) => (
                  <SearchMultiSelectFilter
                    confirm={props.confirm}
                    close={props.close}
                    searchPlaceholder="Search Invoice Status"
                    options={Object.values(FinanceInvoiceFlags).map(
                      (status) => ({
                        label: status,
                        value: status,
                      })
                    )}
                    selectedValues={financeFlags?.invoice?.statuses || []}
                    onApply={(value) => {
                      setFinanceFlags?.({
                        ...financeFlags,
                        invoice: {
                          statuses: value as FinanceInvoiceFlags[],
                        },
                      });
                      props.confirm();
                    }}
                  />
                ),
                render: (_text: unknown, record: CampaignExecutionTableRow) => {
                  if (record.isDeliverableRow) return null;
                  return (
                    <PaymentAmountCell
                      paid={record.financeFlags?.invoice?.paid}
                      total={record.financeFlags?.invoice?.total}
                      status={record.financeFlags?.invoice?.status}
                    />
                  );
                },
              },
              {
                title: "Creator payout",
                dataIndex: "basePayout",
                key: "basePayout",
                width: 220,
                filterCount: financeFlags?.basePayout?.statuses?.length || 0,
                filterDropdownProps: {
                  destroyPopupOnHide: true,
                },
                filtered: !!financeFlags?.basePayout?.statuses?.length,
                filterDropdown: (props: {
                  confirm: () => void;
                  close: () => void;
                }) => (
                  <SearchMultiSelectFilter
                    confirm={props.confirm}
                    close={props.close}
                    searchPlaceholder="Search Payout Status"
                    options={Object.values(FinanceBasePayoutFlags).map(
                      (status) => ({
                        label: status,
                        value: status,
                      })
                    )}
                    selectedValues={financeFlags?.basePayout?.statuses || []}
                    onApply={(value) => {
                      setFinanceFlags?.({
                        ...financeFlags,
                        basePayout: {
                          statuses: value as FinanceBasePayoutFlags[],
                        },
                      });
                      props.confirm();
                    }}
                  />
                ),
                render: (_text: unknown, record: CampaignExecutionTableRow) => {
                  if (record.isDeliverableRow) return null;
                  return (
                    <PaymentAmountCell
                      paid={record.financeFlags?.basePayout?.paid}
                      total={record.financeFlags?.basePayout?.total}
                      status={record.financeFlags?.basePayout?.status}
                    />
                  );
                },
              },
              {
                title: "GST payout",
                dataIndex: "gstPayout",
                key: "gstPayout",
                width: 220,
                filterCount: financeFlags?.gstPayout?.statuses?.length || 0,
                filterDropdownProps: {
                  destroyPopupOnHide: true,
                },
                filtered: !!financeFlags?.gstPayout?.statuses?.length,
                filterDropdown: (props: {
                  confirm: () => void;
                  close: () => void;
                }) => (
                  <SearchMultiSelectFilter
                    confirm={props.confirm}
                    close={props.close}
                    searchPlaceholder="Search GST Payout Status"
                    options={Object.values(FinanceGstPayoutFlags).map(
                      (status) => ({
                        label: status,
                        value: status,
                      })
                    )}
                    selectedValues={financeFlags?.gstPayout?.statuses || []}
                    onApply={(value) => {
                      setFinanceFlags?.({
                        ...financeFlags,
                        gstPayout: {
                          statuses: value as FinanceGstPayoutFlags[],
                        },
                      });
                    }}
                  />
                ),
                render: (_text: unknown, record: CampaignExecutionTableRow) => {
                  if (record.isDeliverableRow) return null;
                  return (
                    <PaymentAmountCell
                      paid={record.financeFlags?.gstPayout?.paid}
                      total={record.financeFlags?.gstPayout?.total}
                      status={record.financeFlags?.gstPayout?.status}
                    />
                  );
                },
              },
              // {
              //   title: "Actions",
              //   dataIndex: "paymentActions",
              //   key: "paymentActions",
              //   width: 80,
              //   fixed: "left" as const,
              //   render: (text: any, record: BriefCampaignInf) => (
              //     <Flex justify="space-between" vertical gap={4}>
              //       <Button
              //         type="default"
              //         size="small"
              //         style={{ padding: "5.25px 10.5px", fontSize: 12 }}
              //         onClick={() => {
              //           setOpenCampaignBriefInfluencerDrawerProps({
              //             open: true,
              //             selectedCampaignInfluencerId: record._id,
              //           });
              //         }}
              //       >
              //         View
              //       </Button>
              //       <Button
              //         size="small"
              //         onClick={() =>
              //           setPaymentModalProps({
              //             visible: true,
              //             influencerId: record._id,
              //           })
              //         }
              //       >
              //         Payment
              //       </Button>
              //     </Flex>
              //   ),
              // },
            ],
          },
        ]
      : [
          ...buildStageColumns({
            selectedDeliverableId,
            config,
            facets,
            filters,
            setFilters,
            onOpenTaskDrawer: handleOpenTaskDrawer,
          }),
          {
            title: "Deliverables",
            key: "deliverables",
            width: 180,
            render: (_: unknown, record: CampaignExecutionTableRow) => {
              if (record.isDeliverableRow) {
                return null;
              }
              return (
                <DeliverablePillSummary
                  deliverable={{
                    purchaseItems: record.purchaseItems ?? [],
                    briefId: briefId,
                  }}
                />
              );
            },
          },
          ...(brief?.visibileOperationsCustomFieldsV2 || []).map((field) => {
            const currentSort = filters?.sortByDynamicFields?.[field.key];
            const sortUI =
              field.type === "number" ? (
                <ColumnSortUI
                  sortActive={!!currentSort}
                  sortDirection={
                    currentSort === SORT_ORDER.ASCEND
                      ? "asc"
                      : currentSort === SORT_ORDER.DESCEND
                        ? "desc"
                        : undefined
                  }
                  onSortChange={(dir) => {
                    if (!setFilters) return;
                    const newSort =
                      dir === "asc"
                        ? SORT_ORDER.ASCEND
                        : dir === "desc"
                          ? SORT_ORDER.DESCEND
                          : undefined;
                    setFilters((prev) => {
                      const newSortByDynamicFields = {
                        ...prev.sortByDynamicFields,
                      };
                      if (newSort === undefined) {
                        delete newSortByDynamicFields[field.key];
                      } else {
                        newSortByDynamicFields[field.key] = newSort;
                      }
                      return {
                        ...prev,
                        sortByDynamicFields: newSortByDynamicFields,
                      };
                    });
                  }}
                />
              ) : null;

            const isDynamicFiltered =
              ((filters?.dynamicFields?.[field.key] as { equalTo?: string[] })
                ?.equalTo?.length || 0) > 0 ||
              (
                filters?.dynamicFields?.[field.key] as {
                  range?: { from?: number; to?: number };
                }
              )?.range?.from !== undefined ||
              (
                filters?.dynamicFields?.[field.key] as {
                  range?: { from?: number; to?: number };
                }
              )?.range?.to !== undefined ||
              !!filters?.sortByDynamicFields?.[field.key];

            const dynamicFilterCount =
              ((filters?.dynamicFields?.[field.key] as { equalTo?: string[] })
                ?.equalTo?.length || 0) +
              ((
                filters?.dynamicFields?.[field.key] as {
                  range?: { from?: number; to?: number };
                }
              )?.range?.from !== undefined ||
              (
                filters?.dynamicFields?.[field.key] as {
                  range?: { from?: number; to?: number };
                }
              )?.range?.to !== undefined
                ? 1
                : 0) +
              (filters?.sortByDynamicFields?.[field.key] ? 1 : 0);

            return {
              title: (
                <Tooltip title={field.displayName}>
                  <span style={{ color: "blue" }}>
                    {trimText(field?.displayName || "", 30)}
                  </span>
                </Tooltip>
              ),
              width: 160,
              dataIndex: field.key,
              key: field.key,
              filterCount: dynamicFilterCount,
              filterDropdownProps: {
                destroyPopupOnHide: true,
              },
              filtered: isDynamicFiltered,

              filterDropdown: (props: {
                confirm: () => void;
                close: () => void;
              }) => {
                if (!setFilters) return null;
                if (field.type === "string") {
                  return (
                    <SearchMultiSelectFilter
                      title={field.displayName}
                      confirm={props.confirm}
                      close={props.close}
                      searchPlaceholder={`Search ${field.displayName || field.key}`}
                      options={(dynamicFacets?.[field.key] || []).map(
                        (item) => ({
                          label: item._id,
                          value: item._id,
                        })
                      )}
                      selectedValues={
                        (
                          filters?.dynamicFields?.[field.key] as {
                            equalTo: string[];
                          }
                        )?.equalTo || []
                      }
                      onApply={(value) => {
                        setFilters((prev) => ({
                          ...prev,
                          dynamicFields: {
                            ...prev.dynamicFields,
                            [field.key]: { equalTo: value },
                          },
                        }));
                      }}
                    />
                  );
                }

                if (field.type === "number") {
                  const currentRange =
                    (
                      filters?.dynamicFields?.[field.key] as {
                        range: { from?: number; to?: number };
                      }
                    )?.range || {};

                  return (
                    <NumberRangeFilter
                      title={field.displayName}
                      before={sortUI}
                      props={props}
                      placeholder={`Select ${field.displayName} range`}
                      selectedRange={currentRange}
                      handleRangeChange={(range) => {
                        setFilters((prev) => ({
                          ...prev,
                          dynamicFields: {
                            ...prev.dynamicFields,
                            [field.key]: {
                              range: range,
                            },
                          },
                        }));
                      }}
                    />
                  );
                }

                return null;
              },

              render: (_: any, record: CampaignExecutionTableRow) => {
                if (record.isDeliverableRow) return null;
                if (
                  !Object.keys(record?.dynamicFields || {}).includes(field.key)
                ) {
                  return <div>-</div>;
                }
                const value = record.dynamicFields?.[field.key];
                const isEditingField =
                  editFieldProps.key === field.key &&
                  record._id === editFieldProps.campaignInfluencerId;

                if (isEditingField) {
                  const handleSave = () => {
                    editDataField({
                      campaignInfluencerId: record._id,
                      dataField: {
                        [editFieldProps.key]: editFieldProps.value || "",
                      },
                    });
                  };

                  const handleCancel = () => {
                    setEditFieldProps({
                      campaignInfluencerId: "",
                      key: "",
                      value: undefined,
                      fieldType: undefined,
                      originalValue: undefined,
                    });
                  };

                  const suffixActions = (
                    <Space
                      size={2}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <Button
                        icon={<Check size={10} />}
                        type="text"
                        size="small"
                        loading={isEditing}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSave();
                        }}
                        className={styles.editActionButton}
                      />
                      <Button
                        icon={<X size={10} />}
                        type="text"
                        size="small"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleCancel();
                        }}
                        className={styles.editActionButton}
                      />
                    </Space>
                  );

                  return field.type === "number" ? (
                    <div className={styles.editFieldWrapper}>
                      <InputNumber
                        controls={false}
                        style={{ width: "100%" }}
                        value={
                          typeof editFieldProps.value === "number"
                            ? editFieldProps.value
                            : editFieldProps.value
                              ? Number(editFieldProps.value)
                              : undefined
                        }
                        onChange={(e) =>
                          setEditFieldProps({
                            ...editFieldProps,
                            value: e || 0,
                          })
                        }
                        onPressEnter={handleSave}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            e.stopPropagation();
                            handleCancel();
                          }
                        }}
                        size="small"
                        className={styles.customFieldInput}
                        suffix={suffixActions}
                        autoFocus
                      />
                      <span className={styles.editShortcutHint}>
                        Enter submit · Esc close
                      </span>
                    </div>
                  ) : (
                    <div className={styles.editFieldWrapper}>
                      <Input
                        style={{ width: "100%" }}
                        value={String(editFieldProps.value || "")}
                        onChange={(e) =>
                          setEditFieldProps({
                            ...editFieldProps,
                            value: e.target.value,
                          })
                        }
                        onPressEnter={handleSave}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            e.stopPropagation();
                            handleCancel();
                          }
                        }}
                        size="small"
                        className={styles.customFieldInput}
                        suffix={suffixActions}
                        autoFocus
                      />
                      <span className={styles.editShortcutHint}>
                        Enter submit · Esc close
                      </span>
                    </div>
                  );
                }

                const rawDisplay =
                  field.type === "number"
                    ? value
                      ? (formatData(Number(value), "a") as string)
                      : "-"
                    : value
                      ? String(value)
                      : "-";

                const isEmptyValue =
                  value === undefined ||
                  value === null ||
                  value === "" ||
                  value === "-" ||
                  rawDisplay === "-";

                const displayValue = isEmptyValue ? "Add value" : rawDisplay;

                return (
                  <div
                    className={classNames(
                      styles.editableCell,
                      isEmptyValue
                        ? styles.editableCellEmpty
                        : styles.editableCellWithValue
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditFieldProps({
                        campaignInfluencerId: record._id,
                        key: field.key,
                        value: value as string | number | undefined,
                        fieldType: field.type as CustomFieldType,
                        originalValue: value as string | number | undefined,
                      });
                    }}
                  >
                    <TextWithTooltip
                      text={displayValue}
                      limit={17}
                      textClassName={
                        isEmptyValue
                          ? styles.emptyValueText
                          : styles.customFieldValue
                      }
                    />
                    <PenLine size={12} className={styles.editIcon} />
                  </div>
                );
              },
            };
          }),
          {
            title: "Ops POC",
            key: "opsPoc",
            width: 150,
            filterCount: assignedOperationsPoc?.length || 0,
            filterDropdownProps: {
              destroyPopupOnHide: true,
            },
            filtered: !!assignedOperationsPoc?.length,
            filterDropdown: (props: {
              confirm: () => void;
              close: () => void;
            }) => {
              const mockFilters: IBriefCampaignInfluencerSearchRequestV2 = {
                briefId: briefId || "",
                assignedOperationsPoc: assignedOperationsPoc,
              };
              const mockSetFilters = (
                value: React.SetStateAction<IBriefCampaignInfluencerSearchRequestV2>
              ) => {
                if (typeof value === "function") {
                  const next = value(mockFilters);
                  setAssignedOperationsPoc?.(next.assignedOperationsPoc);
                } else {
                  setAssignedOperationsPoc?.(value.assignedOperationsPoc);
                }
              };

              return (
                <BriefOpsAssignedFilter
                  {...props}
                  filters={mockFilters}
                  setFilters={mockSetFilters as any}
                  pocOptions={pocOptions || []}
                />
              );
            },
            render: (_: any, record: CampaignExecutionTableRow) => {
              if (record.isDeliverableRow) return null;
              if (record.assignedOperationsPoc) {
                return (
                  <Space>
                    <span>
                      <TextWithTooltip
                        text={`${record.assignedOperationsPoc?.assignedTo?.firstName} ${record.assignedOperationsPoc?.assignedTo?.lastName}`}
                        limit={20}
                        textClassName={styles.opsPocText}
                      />
                    </span>
                  </Space>
                );
              }
              return (
                <span
                  style={{
                    color: "var(--text-secondary)",
                    // textAlign: "center",
                    transition: "color 0.3s ease",
                    fontSize: 11,
                  }}
                >
                  Pending
                </span>
              );
            },
          },
          {
            title: "Status",
            key: "status",
            width: 150,
            // fixed: "right" as const,
            align: "right" as const,
            render: (_: unknown, record: CampaignExecutionTableRow) => {
              if (record.isDeliverableRow) return null;
              return (
                <ExecutionInfluencerStatus
                  briefInfluencer={record.briefInfluencer}
                />
              );
            },
          },
        ]),

    {
      title: "Notes",
      key: "notes",
      // fixed: "right" as const,
      width: 168,
      render: (_: unknown, record: CampaignExecutionTableRow) => {
        if (record.isDeliverableRow || !record.briefInfluencer) return null;
        return (
          <NotesCell record={record.briefInfluencer} refetch={() => {}} />
        );
      },
    },
    {
      title: "",
      key: "actions",
      // fixed: "right" as const,
      width: 50,
      render: (_, record: CampaignExecutionTableRow) => {
         if (record.isDeliverableRow && record.deliverableItem) {
          return (
            <DeliverableViewCell
              onOpenDeliverable={() => {
                setOpenCampaignBriefInfluencerDrawerProps({
                  open: true,
                  selectedCampaignInfluencerId:
                    record.deliverableItem?.parentRecord?._id ||
                    record._id,
                  deliverableConfigRefId: record.deliverableItem?.id,
                });
              }}
            />
          );
        }
        // if (record.isDeliverableRow) return null;
        const isHovered =
          highlightedRecordId === record.briefInfluencerId ||
          highlightedRecordId === record._id;

        if (!isHovered) {
          return null;
        }

        return (
          <Dropdown
            menu={{
              style: {
                minWidth: 100,
                width: 100,
              },
              items: [
                {
                  label: "View",
                  key: "view",
                },
                // ...(showPaymentColumns ? [
                {
                  label: "Payment",
                  key: "payment",
                },
                // ] : [])
              ],
              onClick: ({ key }) => {
                if (key === "view") {
                  // open drawer
                  setOpenCampaignBriefInfluencerDrawerProps({
                    open: true,
                    selectedCampaignInfluencerId: record._id,
                    deliverableConfigRefId: record.deliverableItem?.id,
                  });
                } else if (key === "payment") {
                  // open payment modal
                  setPaymentModalProps({
                    visible: true,
                    influencerId: record._id,
                  });
                }
              },
            }}
          >
            <Button
              icon={<MoreOutlined />}
              size="small"
              shape="circle"
              type="text"
            />
          </Dropdown>
        );
      },
    },
  ];

  const handleScroll = useCallback(() => {
    const bodyEl =
      scrollRef.current?.querySelector<HTMLDivElement>(
        ".ant-table-tbody-virtual-holder"
      ) ??
      scrollRef.current?.querySelector<HTMLDivElement>(".ant-table-body") ??
      scrollRef.current?.querySelector<HTMLDivElement>(
        ".rc-virtual-list-holder"
      );
    if (!bodyEl || isLoadingMore || isLoadingRef.current) return;

    const nearBottom =
      bodyEl.scrollTop + bodyEl.clientHeight >= bodyEl.scrollHeight - 120;
    if (nearBottom) {
      isLoadingRef.current = true;
      onLoadMore();
    }
  }, [isLoadingMore, onLoadMore]);

  useEffect(() => {
    if (!isLoadingMore) {
      isLoadingRef.current = false;
    }
  }, [isLoadingMore]);

  // Attach scroll listener - try multiple selectors, retry when table structure is ready
  useEffect(() => {
    const selectors = [
      ".ant-table-tbody-virtual-holder",
      ".ant-table-body",
      ".rc-virtual-list-holder",
    ];
    let body: HTMLDivElement | null = null;
    for (const sel of selectors) {
      body = scrollRef.current?.querySelector<HTMLDivElement>(sel) ?? null;
      if (body) break;
    }
    const targetBody = body;
    if (!targetBody) return;
    targetBody.addEventListener("scroll", handleScroll);
    return () => targetBody.removeEventListener("scroll", handleScroll);
  }, [
    handleScroll,
    config,
    campaignInfluencers.length,
    displayedCampaignInfluencers.length,
    isLoadingFirstPage,
  ]);

  useEffect(() => {
    if (isEdited) {
      setEditFieldProps({
        campaignInfluencerId: "",
        key: "",
        value: undefined,
        fieldType: undefined,
        originalValue: undefined,
      });
    }
  }, [isEdited]);

  return (
    <div className={styles.tableWrapper} ref={scrollRef}>
      <div className={styles.viewModeHeaderContainer}>
        {title}
        <CampaignExecutionDeliverableTabs
          config={config}
          selectedDeliverableId={selectedDeliverableId}
          onSelectDeliverable={handleSelectDeliverable}
          filters={filters}
          setFilters={setFilters}
          isAllExpanded={viewMode === "table" ? isAllExpanded : undefined}
          onToggleExpandAll={
            viewMode === "table" ? handleToggleExpandAll : undefined
          }
        />
      </div>

      {viewMode === "table" ? (
        <>
          <Table<CampaignExecutionTableRow>
        className={styles.table}
        loading={isLoadingFirstPage}
        rowKey={(record) => record?._id}
        // bordered
        columns={columns}
        dataSource={executionTreeData}
        showHeader={true}
        pagination={false}
        rowSelection={rowSelection}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (newKeys) => setExpandedRowKeys([...newKeys]),
          showExpandColumn: false,
          expandIcon: () => null,
        }}
        onRow={(record, index) => {
          if ((record as any).isDeliverableRow) {
            return {
              onClick: (e) => e.stopPropagation(),
            };
          }
          const actualIndex = displayedCampaignInfluencers.findIndex(
            (inf) => inf._id === record._id
          );
          const rowIndex = actualIndex >= 0 ? actualIndex : (index ?? 0);


              return {
                "data-row-index": String(rowIndex),
                "data-row-id": record._id,
                onMouseDown: (e) => {
                  const target = e.target as HTMLElement;

              if (
                !target.closest("button") &&
                !target.closest("input") &&
                !target.closest(".ant-dropdown") &&
                !target.closest(".ant-select") &&
                !target.closest("a") &&
                !target.closest(".ant-table-row-expand-icon") &&
                !target.closest(`.${styles.expandArrowBtn}`)
              ) {
                handleMouseDown(rowIndex, e);
              }
            },
            onMouseEnter: () => {
              if (isSelecting) {
                handleMouseEnterRow(rowIndex);
                return;
              }
              setHighlightedRecordId(record.briefInfluencerId || record._id);
            },
            onMouseMove: () => {
              if (isSelecting) {
                handleMouseMove(rowIndex);
              }
            },
            onMouseUp: (e) => {
              if (isSelecting) {
                e.stopPropagation();
                handleMouseUp(e);
              }
            },
            onMouseLeave: () => {
              if (!isSelecting) {
                setHighlightedRecordId("");
              }
            },
            style: {
              cursor: isSelecting ? "crosshair" : "default",
              userSelect: isSelecting ? "none" : "auto",
            },
          };
        }}
        scroll={{
          y: 1000,
          x: "max-content",
        }}
        rowClassName={(record) => {
          if ((record as any).isDeliverableRow) {
            return classNames(styles.deliverableChildRow);
          }
          return classNames(styles.tableRow);
        }}

        locale={{
          emptyText: isLoadingFirstPage ? null : (
            <CampaignExecutionEmptyState
              activeStatusFilter={activeStatusFilter}
            />
          ),
        }}
      />

          {isLoadingMore && (
            <Flex justify="center" align="center">
              <div className={styles.loader}>
                <Loader2 size={16} className={styles.loaderIcon} />
                Loading more creators...
              </div>
            </Flex>
          )}
        </>
      ) : (
        <CampaignExecutionGantt
          config={config}
          filters={filters}
          searchQuery={searchQuery}
          activePendingReviewFlag={activePendingReviewFlag}
          isVendorBillingAttached={isVendorBillingAttached}
          financeFlags={financeFlags}
          assignedOperationsPoc={assignedOperationsPoc}
          activeStatusFilter={activeStatusFilter}
          selectedDeliverableId={selectedDeliverableId}
          onOpenDeliverable={(creatorId, deliverableId, taskId) => {
            setOpenCampaignBriefInfluencerDrawerProps({
              open: true,
              selectedCampaignInfluencerId: creatorId,
              deliverableConfigRefId: deliverableId,
              taskId,
            });
          }}
          scrollY={1000}
        />
      )}

      <CampaignBriefInfluencerDrawer
        open={openCampaignBriefInfluencerDrawerProps.open}
        onClose={() =>
          setOpenCampaignBriefInfluencerDrawerProps({
            open: false,
            selectedCampaignInfluencerId: undefined,
            deliverableConfigRefId: undefined,
            taskId: undefined,
          })
        }
        campaignInfluencerId={
          openCampaignBriefInfluencerDrawerProps.selectedCampaignInfluencerId
        }
        deliverableConfigRefId={
          openCampaignBriefInfluencerDrawerProps.deliverableConfigRefId
        }
        taskId={openCampaignBriefInfluencerDrawerProps.taskId}
      />

      {draftDrawerProps.open && (
        <CampaignDraftDrawer
          open={draftDrawerProps.open}
          onClose={() => setDraftDrawerProps({ open: false, draftId: "" })}
          draftId={draftDrawerProps.draftId}
        />
      )}

      {scriptDrawerProps.open && (
        <CampaignScriptingDrawer
          open={scriptDrawerProps.open}
          onClose={() => setScriptDrawerProps({ open: false, scriptingId: "" })}
          scriptingId={scriptDrawerProps.scriptingId}
        />
      )}

      <Drawer
        title="Payment"
        destroyOnClose
        footer={null}
        width={1200}
        open={paymentModalProps.visible}
        onClose={() =>
          setPaymentModalProps({
            ...paymentModalProps,
            visible: false,
          })
        }
      >
        {paymentModalProps.visible && (
          <VendorPayment
            campaignInfluencerId={paymentModalProps.influencerId || ""}
            onClose={() =>
              setPaymentModalProps({
                ...paymentModalProps,
                visible: false,
              })
            }
          />
        )}
      </Drawer>
      {/* <Modal
        title={"View Deliverable"}
        open={openPackagesProps.open}
        onCancel={() =>
          setOpenPackagesProps({
            open: false,
            selectedBriefInfluencer: undefined,
          })
        }
        footer={null}
        width={800}
        destroyOnClose
      >
        <DeliverablePackageCard
          briefId={briefId}
          defaultDeliverableId={brief?.defaultDeliverableV2Id}
          showMarkDefaultButton
          onClose={() =>
            setOpenPackagesProps({
              open: false,
              selectedBriefInfluencer: undefined,
            })
          }
          isEditable={false}
          deliverableId={
            openPackagesProps.selectedBriefInfluencer?.deliverable?._id
          }
          briefInfluencerIds={[
            openPackagesProps.selectedBriefInfluencer?._id || "",
          ]}
        />
      </Modal> */}
    </div>
  );
}

// Empty State Component
